import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { ArrowRight, Cpu, Brain, Shield, Zap, Sparkles, ChevronDown, Languages } from "lucide-react";
import AnimatedBrain from "@/components/AnimatedBrain";
import { useLanguage, LANGUAGES } from "@/contexts/LanguageContext";
import { translate } from "@/utils/translations";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// NeuroMesh Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_e21b96fc-2063-4121-8d2d-76327032f520/artifacts/31nh1s2f_NeuroMesh%20Logo%20%282%29.png";

const Landing = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [authMode, setAuthMode] = useState("signin"); // signin or signup
  const [showEmailAuth, setShowEmailAuth] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: ""
  });
  const { language, setLanguage } = useLanguage();
  const popupRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const t = (text) => translate(text, language);

  // Using popup for frictionless auth - stays on same page
  const handleGoogleLogin = () => {
    setIsLoading(true);
    
    // Calculate popup center position
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    
    // Use a special callback URL that the popup will redirect to
    const redirectUrl = window.location.origin + '/auth-callback';
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    
    // Open popup
    popupRef.current = window.open(
      authUrl,
      'NeuroMesh Login',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );
    
    // Poll for popup closure or successful auth
    pollIntervalRef.current = setInterval(() => {
      try {
        // Check if popup is closed
        if (popupRef.current && popupRef.current.closed) {
          clearInterval(pollIntervalRef.current);
          setIsLoading(false);
          return;
        }
        
        // Try to read the popup URL (will fail due to CORS until redirect back)
        const popupUrl = popupRef.current?.location?.href;
        if (popupUrl && popupUrl.includes('session_id=')) {
          clearInterval(pollIntervalRef.current);
          const sessionId = popupUrl.split('session_id=')[1]?.split('&')[0]?.split('#')[0];
          popupRef.current.close();
          handleAuthCallback(sessionId);
        }
      } catch (e) {
        // Cross-origin error expected, continue polling
      }
    }, 500);
  };
  
  // Listen for message from popup
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'AUTH_SUCCESS' && event.data?.sessionId) {
        clearInterval(pollIntervalRef.current);
        if (popupRef.current) popupRef.current.close();
        handleAuthCallback(event.data.sessionId);
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
  
  const handleAuthCallback = async (sessionId) => {
    try {
      const res = await fetch(`${API}/auth/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId })
      });
      
      if (!res.ok) throw new Error("Auth failed");
      
      const user = await res.json();
      setIsLoading(false);
      navigate("/dashboard", { state: { user }, replace: true });
    } catch (err) {
      console.error("Auth error:", err);
      setIsLoading(false);
    }
  };

  const handleGuestMode = () => {
    navigate("/dashboard?demo=true");
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    
    if (authMode === "signup") {
      if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
        toast.error("Please fill in all fields");
        return;
      }
    } else {
      if (!formData.email || !formData.password) {
        toast.error("Please enter email and password");
        return;
      }
    }
    
    setIsLoading(true);
    try {
      const res = await fetch(`${API}/auth/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          first_name: formData.firstName,
          last_name: formData.lastName,
          mode: authMode
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Authentication failed");
      }
      
      const user = await res.json();
      toast.success(authMode === "signup" ? "Account created successfully!" : "Signed in successfully");
      
      // Navigate with user data
      setTimeout(() => {
        navigate("/dashboard", { state: { user }, replace: true });
      }, 500);
    } catch (err) {
      console.error("Email auth error:", err);
      setIsLoading(false);
      toast.error(err.message || "Authentication failed");
    }
  };

  const features = [
    {
      icon: Cpu,
      title: t("Node Management"),
      description: t("Monitor and control your fleet of humanoid robots and edge devices")
    },
    {
      icon: Brain,
      title: t("AI Inference"),
      description: t("Run distributed inference with cryptographic proof verification")
    },
    {
      icon: Shield,
      title: t("Secure Enclaves"),
      description: t("Hardware-attested security for sensitive robotic operations")
    },
    {
      icon: Zap,
      title: t("Real-time Telemetry"),
      description: t("Stream live data from your entire robotic network")
    }
  ];

  return (
    <div className="min-h-screen bg-[#030303] relative overflow-hidden" data-testid="landing-page">
      {/* Background effects */}
      <div className="absolute inset-0 grid-scan opacity-30" />
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          background: 'radial-gradient(ellipse at 50% 0%, rgba(0, 210, 255, 0.15) 0%, transparent 60%)'
        }}
      />

      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <header className="flex items-center justify-between px-6 lg:px-12 py-6">
          <div className="flex items-center gap-3">
            <img 
              src={LOGO_URL} 
              alt="NeuroMesh" 
              className="w-9 h-9 object-cover cursor-pointer"
              onClick={() => navigate("/")}
            />
            <span className="font-heading text-xl font-bold tracking-tight text-white cursor-pointer" onClick={() => navigate("/")}>
              NEURO<span className="nm-gradient-text">MESH</span>
            </span>
          </div>
          
          
          {/* Language Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                  variant="outline" 
                  size="sm" 
                  className="h-9 gap-2 border-white/20 bg-transparent text-gray-300 hover:bg-white/5 hover:text-white"
                >
                  {LANGUAGES.find(l => l.code === language)?.flag || '🌐'}
                  <span className="hidden sm:inline">{LANGUAGES.find(l => l.code === language)?.name || 'English'}</span>
                  <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#0A0A0A] border-white/10">
              {LANGUAGES.map((lang) => (
                <DropdownMenuItem 
                  key={lang.code} 
                  onClick={() => {
                    setLanguage(lang.code);
                    toast.success(`Language changed to ${lang.name}`);
                    window.location.reload();
                  }}
                  className="text-gray-300 hover:text-white focus:text-white focus:bg-white/5"
                >
                  <span className="text-lg mr-2">{lang.flag}</span>
                  {lang.name}
                  {language === lang.code && <span className="ml-auto text-cyan-400">✓</span>}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Hero */}
        <main className="max-w-6xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Text content */}
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00D2FF]/10 border border-[#00D2FF]/20 mb-6">
                <span className="w-2 h-2 rounded-full bg-[#00D2FF] animate-pulse" />
                <span className="text-[#00D2FF] text-xs font-mono uppercase tracking-wider">{t("Network Active")}</span>
              </div>

              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                {t("The Intelligence Layer for")} <br/>
                <span className="nm-gradient-text text-glow">{t("On-Robot Compute")}</span>
              </h1>

              <p className="text-gray-400 text-base lg:text-lg leading-relaxed mb-8 max-w-lg">
                {t("Orchestrate your robotic fleet with cryptographic proofs, decentralized intelligence, and real-time telemetry. The future of autonomous systems starts here.")}
              </p>

              <div className="flex flex-col gap-3">
                <Button
                  onClick={handleGoogleLogin}
                  disabled={isLoading}
                  className="w-full h-14 px-8 bg-[#00D2FF] hover:bg-[#33e0ff] text-black font-bold uppercase tracking-wider transition-all hover:shadow-[0_0_40px_rgba(0,210,255,0.6)] text-lg"
                  data-testid="google-login-btn"
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      {t("Connecting...")}
                    </span>
                  ) : (
                    <span className="flex items-center gap-3">
                      {t("Continue with Google")}
                      <ArrowRight className="w-5 h-5" />
                    </span>
                  )}
                </Button>

                <Button
                  onClick={handleGuestMode}
                  variant="ghost"
                  className="h-9 px-4 text-gray-500 hover:text-gray-300 text-sm"
                >
                  {t("Continue as Guest")} →
                </Button>
              </div>

              {/* Email Auth Section */}
              <div className="mt-6">
                {!showEmailAuth ? (
                  <button
                    onClick={() => setShowEmailAuth(true)}
                    className="text-sm text-gray-400 hover:text-cyan-400 transition-colors underline"
                  >
                    {t("Or sign in with email")}
                  </button>
                ) : (
                  <div className="glass border border-cyan-500/20 rounded-xl p-6">
                    {/* Toggle Sign In / Sign Up */}
                    <div className="flex gap-2 mb-4">
                      <button
                        onClick={() => setAuthMode("signin")}
                        className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                          authMode === "signin"
                            ? "bg-cyan-500 text-black"
                            : "bg-white/5 text-gray-400 hover:bg-white/10"
                        }`}
                      >
                        Sign In
                      </button>
                      <button
                        onClick={() => setAuthMode("signup")}
                        className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                          authMode === "signup"
                            ? "bg-cyan-500 text-black"
                            : "bg-white/5 text-gray-400 hover:bg-white/10"
                        }`}
                      >
                        Sign Up
                      </button>
                    </div>

                    <form onSubmit={handleEmailAuth} className="space-y-3">
                      {authMode === "signup" && (
                        <div className="grid grid-cols-2 gap-3">
                          <input
                            type="text"
                            value={formData.firstName}
                            onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                            placeholder="First name"
                            className="h-10 px-4 bg-[#0A0A0A] border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50"
                          />
                          <input
                            type="text"
                            value={formData.lastName}
                            onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                            placeholder="Last name"
                            className="h-10 px-4 bg-[#0A0A0A] border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50"
                          />
                        </div>
                      )}
                      
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="Email address"
                        className="w-full h-10 px-4 bg-[#0A0A0A] border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50"
                      />
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="Password"
                        className="w-full h-10 px-4 bg-[#0A0A0A] border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50"
                      />
                      <Button
                        type="submit"
                        disabled={isLoading}
                        className="w-full h-10 bg-cyan-500 hover:bg-cyan-600 text-black font-semibold"
                      >
                        {isLoading ? (
                          <span className="flex items-center gap-2">
                            <span className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                            {authMode === "signup" ? "Creating..." : "Signing in..."}
                          </span>
                        ) : (
                          authMode === "signup" ? "Create Account" : "Sign In"
                        )}
                      </Button>
                      <button
                        type="button"
                        onClick={() => setShowEmailAuth(false)}
                        className="text-xs text-gray-500 hover:text-gray-400 w-full text-center"
                      >
                        Cancel
                      </button>
                    </form>
                  </div>
                )}
              </div>

              <p className="text-gray-600 text-xs mt-4">
                {t("By continuing, you agree to the NeuroMesh Terms of Service")}
              </p>
            </div>

            {/* Right: Animated Brain */}
            <div className="relative hidden lg:flex items-center justify-center animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <AnimatedBrain className="w-80 h-80" />

              {/* Floating stats */}
              <div className="absolute -left-8 top-1/4 glass rounded-lg p-3 animate-slide-in-left">
                <p className="text-xs text-gray-400 font-mono">{t("ACTIVE NODES")}</p>
                <p className="text-xl font-bold text-[#00D2FF] font-mono">24,847</p>
              </div>

              <div className="absolute -right-8 bottom-1/3 glass rounded-lg p-3 animate-slide-in-left" style={{ animationDelay: '0.3s' }}>
                <p className="text-xs text-gray-400 font-mono">{t("INFERENCE/SEC")}</p>
                <p className="text-xl font-bold text-[#00FF94] font-mono">3,421</p>
              </div>
            </div>
          </div>

          {/* Features grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-24">
            {features.map((feature, idx) => (
              <div 
                key={feature.title}
                className="glass rounded-xl p-5 card-hover animate-fade-in-up"
                style={{ animationDelay: `${0.1 * idx}s` }}
              >
                <div className="w-10 h-10 rounded-lg bg-[#00D2FF]/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-5 h-5 text-[#00D2FF]" />
                </div>
                <h3 className="font-heading text-white font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* Try Cerebro CTA */}
          <div 
            className="mt-16 relative overflow-hidden rounded-2xl border border-[#00D2FF]/30 bg-gradient-to-r from-[#00D2FF]/10 via-[#3399FF]/5 to-[#00D2FF]/10 p-8 lg:p-12 cursor-pointer hover:border-[#00D2FF]/50 transition-all group animate-fade-in-up"
            onClick={() => navigate("/cerebro?demo=true")}
            style={{ animationDelay: '0.5s' }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#00D2FF]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative flex flex-col lg:flex-row items-center gap-8">
              <div className="w-20 h-20 lg:w-24 lg:h-24 rounded-2xl bg-gradient-to-br from-[#00D2FF] to-[#3399FF] flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                <Brain className="w-10 h-10 lg:w-12 lg:h-12 text-black" />
              </div>
              <div className="flex-1 text-center lg:text-left">
                <div className="flex items-center justify-center lg:justify-start gap-3 mb-2">
                  <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white">{t("Meet Cerebro")}</h2>
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#00FF94]/20 text-[#00FF94] text-xs font-medium">
                    <Sparkles className="w-3 h-3" />
                    OPERATIONAL
                  </span>
                </div>
                <p className="text-gray-400 text-base lg:text-lg max-w-2xl">
                  {t("Your intelligent assistant with 10 specialized modes — Executive Advisory, Science Mode, Creative Brainstorming, and more. Try it now.")}
                </p>
              </div>
              <Button 
                className="bg-[#00D2FF] hover:bg-[#33e0ff] text-black font-bold px-8 py-6 text-lg group-hover:shadow-[0_0_30px_rgba(0,210,255,0.4)] transition-all"
              >
                {t("Try Cerebro")}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-white/5 py-6 px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-gray-600 text-sm">
            <p>© 2025 NeuroMesh. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <a href="https://whitepaper.neuro-mesh.io" target="_blank" rel="noopener noreferrer" className="hover:text-gray-400 transition-colors">Documentation</a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Landing;
